/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  fetchDashboardFilterData,
  piaExceptionReportColumns,
  reports,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import PiaExceptionReportTableRow from './piaExceptionReportTableRow';

const PiaExceptionReportTable = ({ tableData, totalResults }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [piaExceptionTableData, setPiaExceptionTableData] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);

  const toggleEmailModal = () => {
    if (piaExceptionTableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (piaExceptionTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }

    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        page: page2,
        limit: limit2,
        ...((type === 'search' || searchText.length > 0) && {
          opiOrName: type === 'search' ? value : searchText,
        }),
      };
      const { data } = await getReportData(reports.PIA_EXCEPTION_REPORT, requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount(reports.PIA_EXCEPTION_REPORT, {
          ...fetchDashboardFilterData(filterData),
          opiOrName: type === 'search' ? value : searchText,
        });
        setTotalCount(response?.data?.data?.totalCount);
      }
      setPiaExceptionTableData(data?.data?.items || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );

  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };

  useEffect(() => {
    setPiaExceptionTableData(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
  }, [tableData, totalResults, setPage, setLimit]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Toolbar
        title="PIA Exception Report"
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        onSerchInputChange={onSerchInputChange}
        isSearchable
        SearchInputPlaceholder="OPI, Contract Number, Counselor/Manager"
        isTableDataPresent={piaExceptionTableData?.length > 0}
        toolTipText="The PIA Exception report will show you a list of all the contracts that have been paid in full, but still have a PIA (Partial Interest Adjustment) adjustment pending."
        module="Reports"
      />
      {piaExceptionTableData && piaExceptionTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table
              headers={piaExceptionReportColumns}
              bordered
              responsive
              className="dashboard-table"
            >
              <tbody>
                {piaExceptionTableData?.map((rowData, rowNumber) => {
                  return (
                    <PiaExceptionReportTableRow
                      key={slStartNumber + rowNumber + 1}
                      slNo={slStartNumber + rowNumber + 1}
                      rowData={rowData}
                      id={slStartNumber + rowNumber + 1}
                    />
                  );
                })}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType={reports.PIA_EXCEPTION_REPORT}
        apiValues={{
          ...(searchText && { opiOrName: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={reports.PIA_EXCEPTION_REPORT}
        urlParams={{
          action: 'download',
          ...(searchText && { opiOrName: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </>
  );
};

export default PiaExceptionReportTable;
